<template>
	<div class="mt-4 mb-5">
		<!-- Basic Details - Start -->
		<div class="row">
			<div class="col-12 pr-5 pl-5 mt-4">
				<p>
					Understand which Cloudflare SSL options encrypt HTTPS traffic between Cloudflare and the origin web
					server
				</p>
				<p>
					The SSL section of the Cloudflare Crypto app contains several options that determine whether
					Cloudflare securely connects to your origin web server.
					After reviewing the description of each SSL option, refer to our list of recommended SSL options
					depending on your origin web server SSL configuration:
				</p>
				<p>Off disables HTTPS for your site visitors whereas Full(Strict) provides the most traffic security
					end-to-end.</p>
				<div class="row">
					<div class="col-12">
						<div class="form-group">
							<label for="type" class="mr-3">
								<strong>Change SSL type</strong>
							</label>
							<select class="form-control-sm" id="type" ref="type" @change="changeType" :value="sslType" :disabled="loading">
								<option value="off">Off</option>
								<option value="flexible">Flexible</option>
								<option value="full">Full</option>
								<option value="strict">Full (strict)</option>
							</select>
						</div>
					</div>
				</div>

				<hr/>
				<div class="pb-2">
					<h4>Off</h4>
					<p>
						Off disables secure HTTPS connections between both visitors and Cloudflare and between
						Cloudflare and your origin web server.
						Visitors can only view your website over HTTP. Any connections attempted via HTTPS result in a
						HTTP 301 redirect to unencrypted HTTP.
					</p>
				</div>
				<hr/>
				<div class="pb-2">
					<h4>Flexible</h4>
					<p>
						The Flexible SSL option allows a secure HTTPS connection between your visitor and Cloudflare,
						but forces Cloudflare to connect to your origin web server over unencrypted HTTP.
						An SSL certificate is not required on your origin web server and your visitors will still see
						the site as being HTTPS enabled.
					</p>
					<div class="alert alert-warning rounded rounded-lg text-dark">
						<em>Flexible</em> is not recommended if your website contains sensitive information.
						Use <em>Flexible</em> only as a last resort if you are unable to setup SSL at your origin web
						server.
					</div>
				</div>
				<hr/>
				<div class="pb-2">
					<h4>Full</h4>
					<p>
						Full ensures a secure connection between both the visitor and your Cloudflare domain and between
						Cloudflare and your web server.
					</p>
					<div class="alert alert-warning rounded rounded-lg text-dark">
						The Full <strong>SSL</strong> option does not validate SSL certificate authenticity at the
						origin. A self-signed certificate is allowed at the origin web server.
					</div>
					<p>
						To avoid 525 errors, before enabling Full SSL option, configure your origin web server to allow
						HTTPS connections on port 443 and present either a self-signed SSL certificate, a Cloudflare
						Origin CA certificate, or a valid certificate purchased from a Certificate Authority.
					</p>
				</div>
				<hr/>
				<div>
					<h4>Full (strict)</h4>
					<p>
						Full(strict) ensures a secure connection between both the visitor and your Cloudflare domain and
						between Cloudflare and your origin web server.
						Configure your origin web server to allow HTTPS connections on port 443 and present either a
						Cloudflare Origin CA certificate or a valid certificate purchased from a Certificate Authority.
						This certificate must be signed by a Certificate Authority that is trusted by Cloudflare, have a
						future expiration date, and cover the requested domain name (hostname).
					</p>
					<div class="alert alert-warning rounded rounded-lg text-dark">
						The <em>Full(strict)</em> <strong>SSL</strong> option checks for SSL certificate validity at the
						origin web server. A self-signed certificate cannot be used.
						A Cloudflare Origin CA certificate or valid certificate purchased from a Certificate Authority
						is required to avoid 526 errors.
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import swal from "sweetalert2";
	import refreshToken from "../../../../mixins/refreshTokenMixin";

	export default {
		name: "SSL",
		mixins: [refreshToken],
		props: {
			account: {
				type: Object,
				required: true
			}
		},
		data: function () {
			return {
				loading: false,
				sslType: null
			}
		},
		computed: {
			userToken() {
				return this.$store.getters.getUserToken;
			}
		},
		watch: {
			account: function () {
				this.getSSLSettings();
			},
			envType() {
				this.getSSLSettings();
			}
		},
		mounted() {
			this.getSSLSettings();
		},
		methods: {
			getSSLSettings() {
				this.loading = true;
				const {username, apiKey, zone_id} = this.account.cloudflare;
				this.$http.post(`zone/settings/${zone_id}/ssl`, {
					grant_type: "token",
					token: this.userToken,
					cloudflare_email: username,
					cloudflare_api_key: apiKey
				}, {
					headers: {
						'Content-Type': 'application/json'
					}
				})
					.then((res) => {
						const {data: {value}} = res.data;
						this.sslType = value;
					})
					.catch(() => {
						console.error("Error loading SSL settings");
					})
					.finally(() => {
						this.loading = false;
					});
			},
			changeType(e) {
				if(!e.target.value){
					return false;
				}

				this.loading = true;
				const {username, apiKey, zone_id} = this.account.cloudflare;
				const value = e.target.value;
				this.$http.patch(`zone/settings/${zone_id}/ssl`, {
					grant_type: "token",
					token: this.userToken,
					cloudflare_email: username,
					cloudflare_api_key: apiKey,
					value: value
				}, {
					headers: {
						'Content-Type': 'application/json'
					}
				})
					.then(() => {
						this.sslType = value;
						swal({
							title: "Success!",
							html: "SSL type was updated",
							type: "success"
						})
					})
					.catch(() => {
						swal({
							title: "Error!",
							html: "Error updating SSL type",
							type: "error"
						})
					})
					.finally(() => {
						this.loading = false;
					});

			}
		}
	};
</script>
